import { FunctionComponent, MouseEvent, useEffect, useRef } from "react";
import { ActionPlanRequestItem } from "../../models/ActionPlan";
import { VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import styles from "./ActionPlanRequestDetailModal.module.css";

interface ActionPlanRequestDetailModalProps {
    open: boolean;
    request?: ActionPlanRequestItem;
    onClose: () => void;
}

const ActionPlanRequestDetailModal: FunctionComponent<ActionPlanRequestDetailModalProps> = ({
    open,
    request,
    onClose
}) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open || !request) return null;

    const details = getRequestDetails(request);

    const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) onClose();
    };

    return (
        <div className={styles.backdrop} role="presentation" onMouseDown={handleBackdropClick}>
            <section className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="request-detail-title">
                <button
                    ref={closeButtonRef}
                    type="button"
                    className={styles.closeButton}
                    aria-label="Fermer la fenêtre de détail"
                    onClick={onClose}
                >
                    ×
                </button>

                <div className={styles.badges}>
                    <span className={styles.typeBadge}>{details.type}</span>
                    {request.valueCodes.map((value) => (
                        <span key={value} className={styles.valueBadge}>{formatValue(value)}</span>
                    ))}
                </div>

                <h2 id="request-detail-title">{request.title}</h2>

                <dl className={styles.summaryList}>
                    {request.theme && <div><dt>Thématique</dt><dd>{request.theme}</dd></div>}
                    {request.status && <div><dt>Statut</dt><dd>{request.status}</dd></div>}
                    {request.organizationDisplay && (
                        <div>
                            <dt>{details.type === "Besoin déclaré" ? "Besoin exprimé par" : "Proposé par"}</dt>
                            <dd>{request.organizationDisplay}</dd>
                        </div>
                    )}
                    {details.date && <div><dt>Créé le</dt><dd>{details.date}</dd></div>}
                </dl>

                <section className={styles.detailSection}>
                    <h3>Détail</h3>
                    <p className={details.description ? undefined : styles.emptyDetail}>
                        {details.description ?? "Aucun détail supplémentaire n’a été renseigné."}
                    </p>
                </section>

                <button type="button" className={styles.closeAction} onClick={onClose}>Fermer</button>
            </section>
        </div>
    );
};

const getRequestDetails = (request: ActionPlanRequestItem): {
    type: string;
    date?: string;
    description?: string;
} => {
    const raw = request.raw as any;
    const type = raw.code?.coding?.some((coding: { code?: string }) =>
        ["proposition", "proposition-atelier", "bonne-pratique"].includes(coding.code ?? "")
    ) ? "Solution proposée" : "Besoin déclaré";
    const notes = Array.isArray(raw.note)
        ? raw.note.map((note: { text?: string }) => note.text).filter(Boolean)
        : [];
    const description = notes.join("\n\n") || raw.description || undefined;

    return {
        type,
        date: raw.authoredOn ? formatDate(raw.authoredOn) : undefined,
        description
    };
};

const formatValue = (value: string): string => VALUE_LABELS[value as ValueCode] ?? value;

const formatDate = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric"
}).format(new Date(value));

export default ActionPlanRequestDetailModal;
